import { OrganisationLineItem } from "catchment-common/interfaces/organisation";
import { ProjectAccessLevel } from "catchment-common/entities/project";
import { ProfileLineItem } from "catchment-common/interfaces/profile";
import {
  ProjectDetailLineItem,
  ProjectLineItem
} from "catchment-common/interfaces/project";

export const userIsAdmin = (profile?: ProfileLineItem) => {
  return profile?.isAdmin || false;
};

export const userHasAdminAccess = (
  project?: ProjectDetailLineItem | ProjectLineItem
) => {
  if (!project) return false;
  return project.projectAccessLevel === ProjectAccessLevel.Admin;
};

export const userHasManagerAccess = (
  project?: ProjectDetailLineItem | ProjectLineItem
) => {
  if (!project) return false;
  return (
    project.projectAccessLevel === ProjectAccessLevel.Admin ||
    project.projectAccessLevel === ProjectAccessLevel.Manager
  );
};

export const userHasEditorAccess = (
  project?: ProjectDetailLineItem | ProjectLineItem
) => {
  if (!project) return false;
  return (
    project.projectAccessLevel === ProjectAccessLevel.Admin ||
    project.projectAccessLevel === ProjectAccessLevel.Manager ||
    project.projectAccessLevel === ProjectAccessLevel.Write
  );
};

/**
 * Whether the user may accept or suggest changes on a report's sections.
 *
 * EDITOR access, not manager access — because "Project Admin" does not survive
 * the trip to this side of the wire for everyone it is granted to.
 * `resolveStoredAccessLevel` levels a stored Admin row down by whose project it
 * is: Manager for a user in the project's OWNING organisation, and **Write for
 * a user in a guest organisation**, on the grounds that Project Admin should
 * not carry authority over a project their organisation does not own. Fair for
 * the project itself — the same Admin|Manager pair gates `upsertProject` — but
 * reviewing the reports of packages BELOW you is not authority over the
 * project, and requiring Manager here hid the Accept buttons from every
 * contractor's project admin, subpackage managers included.
 *
 * This is only half the question. Both reviewer surfaces pair it with
 * {@link canReviewPackageReports}, which asks whether the viewer's package sits
 * above the one under review — so "editor on this project AND above that
 * package" is the real rule. Neither half is sufficient alone.
 */
export const canReviewReportSections = (
  profile?: ProfileLineItem,
  project?: ProjectDetailLineItem | ProjectLineItem
) => userIsAdmin(profile) || userHasEditorAccess(project);

export const userHasLevel1OnlyAccess = (
  project?: ProjectDetailLineItem | ProjectLineItem
) => {
  if (!project) return false;
  return project.projectAccessLevel === ProjectAccessLevel.Level1Only;
};

/**
 * Whether the viewer's own package sits ABOVE the package under review, and so
 * may accept or reject its report sections.
 *
 * A head contractor qualifies by package type. A contractor whose organisation
 * has subpackages enabled qualifies too: it issues its own subcontractor
 * packages and reviews their reports exactly as the head contractor reviews
 * its own. Gating the review buttons on `isHeadContractorPackage` alone left
 * those reviewers with no way to accept anything.
 *
 * Same `isHeadContractorPackage || subpackageEnabled` rule the level-2
 * drill-downs, targets tabs and analytics filters already use.
 */
export const canReviewPackageReports = (
  isHeadContractorPackage: boolean,
  organisation?: OrganisationLineItem
) => isHeadContractorPackage || Boolean(organisation?.subpackageEnabled);

/**
 * Whether the viewer may skip a package's monthly report: the head contractor,
 * for any package including its own, or the package owner that issued it — a
 * subpackage manager for the subpackages below it. Never a contractor for its
 * own report otherwise. Editor access is required either way.
 *
 * `viewerPackageId` is the viewer's own package (the route's
 * `:contractorPackageId`); `packageParentId` is the parent of the package being
 * viewed. The server enforces the same rule; see
 * docs/adr/0003-skipping-reports.md in the workspace.
 */
export const canSkipPackageReports = ({
  isHeadContractorPackage,
  packageParentId,
  profile,
  project,
  viewerPackageId
}: {
  isHeadContractorPackage: boolean;
  packageParentId?: string;
  profile?: ProfileLineItem;
  project?: ProjectDetailLineItem | ProjectLineItem;
  viewerPackageId?: string;
}) =>
  (userIsAdmin(profile) || userHasEditorAccess(project)) &&
  (isHeadContractorPackage ||
    (viewerPackageId !== undefined && packageParentId === viewerPackageId));

/**
 * Compliance answers "is this PROJECT meeting the policies it committed to" —
 * the head contractor's question and the client's, not a subcontractor's.
 *
 * The flag describes the package in the URL, which stays the VIEWER's own —
 * `/package-details/:projectId/:contractorPackageId/:packageId` inspects a
 * package without changing context — so a head contractor keeps Compliance
 * while drilling into a subcontractor's package.
 *
 * Two admissions because the two are identified differently: the head
 * contractor by package type (taken as the boolean the provider already
 * derives, so it cannot disagree), the client by `Level1Only`, which they can
 * hold on a package that is not the head one. Subcontractors arrive as
 * `Write`/`ReadOnly` on a non-head package and fail both.
 */
export const canViewCompliance = (
  isHeadContractorPackage: boolean,
  project?: ProjectDetailLineItem | ProjectLineItem
): boolean => isHeadContractorPackage || userHasLevel1OnlyAccess(project);
